import { createContext, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import type { User } from 'firebase/auth'
import { onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth'
import { auth, googleProvider } from '../lib/firebase'

/**
 * Admission to this app is an AUTHORIZATION decision, not an authentication one.
 *
 * The admin app and the main app share one Firebase project, and therefore one
 * user pool. Anyone with a Google account can complete a sign-in here — there is
 * no per-app self-registration control, and the project-wide one would block the
 * main app too. So a non-admin gets in the door and is then told no.
 *
 * That is the correct model rather than a compromise: the login screen was never
 * the boundary. The boundary is the `platformAdmin` custom claim, enforced in
 * Firestore rules. Everything below only decides what to render — someone who
 * bypassed it would still be denied every read that matters.
 *
 * Note this is the opposite call from organisation membership, which lives in
 * Firestore precisely because claims go stale for up to an hour. `platformAdmin`
 * changes about never, and the staleness cuts the safe way: granting is slow,
 * revoking is what you want to be fast, and revoking a claim plus revoking the
 * refresh token is available if it ever matters.
 */
export type AdminState =
  | { status: 'loading' }
  | { status: 'signed-out' }
  | { status: 'not-authorised'; user: User }
  | { status: 'admin'; user: User }
  | { status: 'error'; message: string }

interface AdminAuthValue {
  state: AdminState
  signIn: () => Promise<void>
  signOutNow: () => Promise<void>
}

const AdminAuthContext = createContext<AdminAuthValue | null>(null)

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AdminState>({ status: 'loading' })

  useEffect(() => {
    return onAuthStateChanged(auth, (user) => {
      if (!user) {
        setState({ status: 'signed-out' })
        return
      }
      setState({ status: 'loading' })
      // Force a refresh rather than reading the cached token. A custom claim
      // does not appear until the ID token is reissued, so a freshly granted
      // admin would otherwise be told "not authorised" for up to an hour.
      user
        .getIdTokenResult(true)
        .then((result) => {
          setState(
            result.claims['platformAdmin'] === true
              ? { status: 'admin', user }
              : { status: 'not-authorised', user },
          )
        })
        .catch((error: unknown) =>
          setState({
            status: 'error',
            message: error instanceof Error ? error.message : 'Could not verify access.',
          }),
        )
    })
  }, [])

  async function signIn() {
    try {
      await signInWithPopup(auth, googleProvider)
    } catch (error: unknown) {
      const code = (error as { code?: string })?.code
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') return
      setState({
        status: 'error',
        message: error instanceof Error ? error.message : 'Sign-in failed.',
      })
    }
  }

  async function signOutNow() {
    await signOut(auth)
  }

  return (
    <AdminAuthContext.Provider value={{ state, signIn, signOutNow }}>
      {children}
    </AdminAuthContext.Provider>
  )
}

export function useAdminAuth(): AdminAuthValue {
  const context = useContext(AdminAuthContext)
  if (!context) throw new Error('useAdminAuth must be used inside <AdminAuthProvider>')
  return context
}
